import React from 'react';
import { Box, Button, Card, CardContent, Chip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PortalInspectionReport } from '../services/portalApi';

export const InspectionReport: React.FC<{ report: PortalInspectionReport; document?: { url: string; fileName: string } | null }> = ({ report, document }) => {
  const { t } = useTranslation();
  const conditions: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
    satisfactory: { label: 'Satisfactory', color: 'success' },
    needs_attention: { label: 'Needs Attention', color: 'warning' },
    safety_issue: { label: 'Safety Issue', color: 'error' },
  };
  return <>
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h5" component="h1">{t(report.reportTitle)}</Typography>
          {document && <Button component="a" href={document.url} target="_blank" rel="noopener noreferrer" variant="contained">{t('Download PDF')}</Button>}
        </Box>
        {report.welcomeLetter && <Typography sx={{ mt: 2, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{report.welcomeLetter}</Typography>}
        {report.coverPhotoUrl && <Box component="a" href={report.coverPhotoUrl} target="_blank" rel="noopener noreferrer" sx={{ display: 'block', mt: 2 }}>
          <Box component="img" src={report.coverPhotoUrl} alt={t('Inspection cover photo')} sx={{ display: 'block', maxWidth: '100%', maxHeight: 360, borderRadius: 2 }} />
        </Box>}
      </CardContent>
    </Card>
    {report.sections.map(section => <Card key={section.slug} sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" component="h2">{section.label}</Typography>
        {section.items.map(item => {
          const condition = conditions[item.condition || ''] || { label: 'Not rated', color: 'default' as const };
          return <Box key={item.id} sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>{item.label}</Typography>
              <Chip size="small" label={t(condition.label)} color={condition.color} />
            </Box>
            {item.notes && <Typography sx={{ mt: 1, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item.notes}</Typography>}
            {item.photoUrls.length > 0 && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2, mt: 2 }}>
              {item.photoUrls.map((url, index) => <Box key={url} component="a" href={url} target="_blank" rel="noopener noreferrer">
                <Box component="img" src={url} alt={`${item.label} — ${index + 1}`} loading="lazy" sx={{ width: '100%', maxHeight: 320, objectFit: 'contain', borderRadius: 2 }} />
              </Box>)}
            </Box>}
          </Box>;
        })}
      </CardContent>
    </Card>)}
    {report.disclaimer && <Card sx={{ mb: 3 }}><CardContent>
      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{report.disclaimer}</Typography>
    </CardContent></Card>}
  </>;
};
